import { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { ClocksListResponse } from "../../../../Types";
import useClocksInfiniteQuery from "../../../Device/hooks/useClocksInfiniteQuery";
import useListboxInfiniteScroll, {
  noClientFilter,
} from "../../../Device/hooks/useListboxInfiniteScroll";
import useAddClockToUserMutation from "../hooks/useAddClocksToUserMutation";
import useGetUserClocksQuery from "../hooks/useGetUserClocksQuery";
import useClockDetailsByIds from "../../../Gifts/GiftOrders/hooks/useClockDetailsByIds";
import useRemoveClocksFromUserMutation from "../hooks/useRemoveClocksFromUserMutation";
import { sectionCardSx } from "../../../system-design/sectionCardSx";

type UserClockManagementProps = {
  userId: string;
};

const sectionTitleSx = {
  fontSize: 12,
  fontFamily: "Gilroy-SemiBold",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "surface.accentText",
};

const UserClockManagement = ({ userId }: UserClockManagementProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClocksForAdd, setSelectedClocksForAdd] = useState<
    ClocksListResponse[]
  >([]);
  const [selectedClockForRemove, setSelectedClockForRemove] = useState<
    string | null
  >(null);

  // Fetch available clocks for search
  const {
    data: availableClocks,
    isLoading: isLoadingAvailableClocks,
    error: errorAvailableClocks,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClocksInfiniteQuery({ query: searchQuery });

  const { listboxProps } = useListboxInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  // Mutation to add clocks to user
  const {
    mutate: addClockToUser,
    isPending: isAddingClock,
    error: errorAddingClock,
  } = useAddClockToUserMutation();

  // Get user clocks
  const { data: userClocks, error: errorUserClocks } =
    useGetUserClocksQuery(userId);

  // Fetch details for currently attached clocks
  const {
    data: attachedClocks,
    isLoading: isLoadingAttachedClocks,
    error: errorAttachedClocks,
  } = useClockDetailsByIds(userClocks || []);

  // Remove clock from user
  const {
    mutate: removeClockFromUser,
    isPending: isRemovingClock,
    error: errorRemovingClock,
  } = useRemoveClocksFromUserMutation();

  // Filter out already attached clocks from available clocks
  const filteredAvailableClocks = useMemo(() => {
    if (!availableClocks) return [];
    const attachedIds = new Set(userClocks || []);
    return availableClocks.filter((clock) => !attachedIds.has(clock.id));
  }, [availableClocks, userClocks]);

  // Handle adding selected clocks to user
  const handleAddClocks = () => {
    if (!selectedClocksForAdd || selectedClocksForAdd.length === 0) return;

    const clockIds = selectedClocksForAdd.map((clock) => clock.id);
    addClockToUser({
      userId: userId,
      clockIds: clockIds,
    });

    // Clear selection after adding
    setSelectedClocksForAdd([]);
  };

  // Handle removing clock from user
  const handleRemoveClock = (clockId: string) => {
    if (!clockId) return;

    setSelectedClockForRemove(clockId);
    removeClockFromUser(
      {
        userId: userId,
        clockId: clockId,
      },
      {
        onSuccess: () => {
          setSelectedClockForRemove(null);
        },
        onError: () => {
          setSelectedClockForRemove(null);
        },
      },
    );
  };

  return (
    <Box sx={{ height: "100%" }}>
      <Card sx={[{ maxHeight: "90vh", overflow: "auto" }, sectionCardSx]}>
        <CardHeader
          title="Clocks Management"
          slotProps={{
            title: { variant: "h6", sx: sectionTitleSx },
          }}
          sx={{ pb: 0 }}
        />
        <Box
          sx={{
            width: 28,
            height: 2,
            bgcolor: "primary.main",
            borderRadius: 2,
            mt: 0.75,
            mx: 2,
          }}
        />
        <CardContent
          sx={{
            maxHeight: "100%",
            overflow: "auto",
          }}
        >
          {/* Display User Clocks */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...sectionTitleSx }}>
              User Clocks ({userClocks?.length || 0})
            </Typography>

            {errorUserClocks && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Error loading user clocks: {errorUserClocks?.message}
              </Typography>
            )}

            {errorAttachedClocks && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Error loading clock details: {errorAttachedClocks?.message}
              </Typography>
            )}

            {isLoadingAttachedClocks ? (
              <CircularProgress />
            ) : attachedClocks && attachedClocks.length > 0 ? (
              <Box sx={{ gap: 2, mt: 2 }}>
                {attachedClocks.map((clock) => (
                  <Box
                    key={clock.id}
                    sx={{
                      borderBottom: "1px solid",
                      borderBottomColor: "divider",
                      borderRadius: 1,
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: "bold",
                        }}
                      >
                        {clock.idf}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {clock.id}
                      </Typography>
                    </Box>

                    {selectedClockForRemove === clock.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveClock(clock.id)}
                        disabled={
                          selectedClockForRemove !== null || isRemovingClock
                        }
                        sx={{
                          ...(selectedClockForRemove !== null || isRemovingClock
                            ? { cursor: "not-allowed" }
                            : { cursor: "pointer" }),
                        }}
                      >
                        {isRemovingClock ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No clocks assigned to this user
              </Typography>
            )}
          </Box>

          {/* Add Clock Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...sectionTitleSx }}>
              Add Clock
            </Typography>

            {errorAddingClock && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Error adding clocks: {errorAddingClock?.message}
              </Typography>
            )}

            {errorRemovingClock && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Error removing clock: {errorRemovingClock?.message}
              </Typography>
            )}

            {errorAvailableClocks && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Error searching clocks: {errorAvailableClocks?.message}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                sx={{ flexGrow: 1, width: 300 }}
                options={filteredAvailableClocks || []}
                getOptionLabel={(option) => `${option.idf} (${option.id})`}
                value={selectedClocksForAdd}
                onChange={(_, newValue) => setSelectedClocksForAdd(newValue)}
                inputValue={searchQuery}
                onInputChange={(_, newInputValue) =>
                  setSearchQuery(newInputValue)
                }
                loading={isLoadingAvailableClocks || isFetchingNextPage}
                filterOptions={noClientFilter}
                ListboxProps={listboxProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for clocks..."
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingAvailableClocks && (
                            <CircularProgress size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {option.idf}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText={
                  searchQuery ? "No clocks found" : "Type to search for clocks"
                }
              />

              <Button
                variant="contained"
                size="medium"
                startIcon={
                  isAddingClock ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddIcon />
                  )
                }
                onClick={handleAddClocks}
                disabled={
                  !selectedClocksForAdd ||
                  selectedClocksForAdd.length === 0 ||
                  isAddingClock
                }
              >
                Add
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserClockManagement;
